import React, { useCallback, useEffect, useState, useMemo } from "react";
import AdministrationDrawerMenu from "./AdministrationDrawerMenu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Loader from "@civicplus/preamble-ui/lib/Loader";
import NavigationRail from "@civicplus/preamble-ui/lib/NavigationRail";
import NotificationsOrganizationService from "../../provider/notificationsOrganizationService";
import Page from "@civicplus/preamble-ui/lib/Page";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import SvgIcon from "@mui/material/SvgIcon";
import ViewListIcon from "@mui/icons-material/ViewList";
import { bottle } from "../../provider/Bottle";
import { CppOrganization } from "@civicplus/preamble-ui/lib/Services/OrgService/Types/Organization";
import { ConfigOptions } from "../../entities/ConfigOptions";
import { getCppOrganizationFromOrganization } from "../admin/shared/functions";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router";
import { mdiCalendarClock, mdiEmailArrowRightOutline } from "@mdi/js";
import { Organization } from "../../entities/Organization";
import { OrganizationSettings } from "../../entities/OrganizationSettings";
import { ReactComponent as MySubscriptionLogo } from "../../app_registration-24px.svg";
import { User } from "oidc-client-ts";
import { UserManager } from "../../services/UserManager";
import { useStyles } from "./styles";
import { usePendo } from "./pendo";

interface MenuItem {
    children: string;
    onClick: () => Window | void | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withRouter(Component: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function ComponentWithRouterProp(props: any) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} router={{ location, navigate, params }} />;
    }

    return ComponentWithRouterProp;
}

interface PageWrapperProps {
    isCitizenView: boolean;
    currentOrgSettings: OrganizationSettings;
    width?: unknown;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: any;
}

const PageWrapperInternal: React.FC<PageWrapperProps> = ({ isCitizenView, currentOrgSettings, width, children }) => {
    const orgService = bottle.container.OrgService;
    const apiService = bottle.container.ApiService;
    const userManager = bottle.container.UserManager as UserManager;
    const configOptions = bottle.container.ConfigOptions as ConfigOptions;

    const classes = useStyles();
    const [organization, setOrganization] = useState<Organization | null>();
    const [orgId, setOrgId] = useState<string>("");
    const [signoutUrl, setSignoutUrl] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSuperUser, setIsSuperUser] = useState<boolean>(false);
    const [authUser, setAuthUser] = useState<User>();
    const [activeBtn, setActiveBtn] = useState<number>();
    const [showDrawer, setShowDrawer] = useState(false);
    const [orgNavButtons, setOrgNavButtons] = useState([] as React.ComponentProps<typeof NavigationRail>["buttons"]);
    const [forethoughtApiKey, setForethoughtApiKey] = useState<string>("");
    const location = useLocation();

    usePendo(organization ? getCppOrganizationFromOrganization(organization) : undefined, orgService.user);

    useEffect(() => {
        const getUser = async () => {
            const user = await userManager.getUserAsync();
            setAuthUser(user);
        };

        getUser();
    }, [userManager]);

    const buildNavigation = useCallback(() => {
        let orgAppNavBtns: React.ComponentProps<typeof NavigationRail>["buttons"] = [];

        if (isSuperUser || organization?.isOrgOwner || organization?.hasAccess) {
            orgAppNavBtns.push({
                label: "Dashboard",
                icon: <DashboardIcon />,
                buttonProps: {
                    id: "Dashboard",
                    to: `/${orgId}/admin`,
                    component: RouterLink
                }
            });

            orgAppNavBtns.push({
                label: "Pending Messages",
                icon: (
                    <SvgIcon color="inherit">
                        <path d={mdiCalendarClock} />
                    </SvgIcon>
                ),
                buttonProps: {
                    id: "PendingMessages",
                    to: `/${orgId}/admin/messages/pending`,
                    component: RouterLink
                }
            });

            orgAppNavBtns.push({
                label: "Sent Messages",
                icon: (
                    <SvgIcon color="inherit">
                        <path d={mdiEmailArrowRightOutline} />
                    </SvgIcon>
                ),
                buttonProps: {
                    id: "SentMessages",
                    to: `/${orgId}/admin/messages/history`,
                    component: RouterLink
                }
            });

            orgAppNavBtns.push({
                label: "Subscribers",
                icon: <PeopleIcon />,
                buttonProps: {
                    id: "Subscribers",
                    to: `/${orgId}/admin/subscribers`,
                    component: RouterLink
                }
            });

            orgAppNavBtns.push({
                label: "Subscription Lists",
                icon: <ViewListIcon />,
                buttonProps: {
                    id: "SubscriptionLists",
                    to: `/${orgId}/admin/lists`,
                    component: RouterLink
                }
            });
        }

        orgAppNavBtns.push({
            label: "My Subscriptions",
            icon: (
                <SvgIcon color="inherit">
                    <MySubscriptionLogo />
                </SvgIcon>
            ),
            buttonProps:
                bottle.container?.FeatureFlags?.manageMySubscriptions === false
                    ? {
                          id: "MySubscriptions",
                          onClick: () => {
                              window.open(`${configOptions.portalBaseUrl}/${orgId}/notifications`, "_blank");
                          }
                      }
                    : { id: "MySubscriptions", to: `/${orgId}/lists`, component: RouterLink }
        });

        if (isSuperUser || organization?.isOrgOwner || organization?.hasAccess) {
            orgAppNavBtns.push({
                label: "Administration",
                icon: <SettingsIcon />,
                isAdmin: true,
                drawer: {
                    open: showDrawer,
                    classes: { paper: classes.drawerPaper },
                    className: classes.drawer,
                    onClose: () => {
                        setShowDrawer(false);
                        onNavChange(undefined);
                    },
                    id: "administration-drawer",
                    variant: "temporary",
                    children: (
                        <AdministrationDrawerMenu
                            orgId={orgId}
                            onClick={setShowDrawer}
                            isSuperUser={isSuperUser}
                            isOrgOwner={organization?.isOrgOwner}
                            hasAccess={organization?.hasAccess}
                        />
                    )
                }
            });
        }

        orgAppNavBtns = orgAppNavBtns.sort((a, b) => {
            if (a.label === "Dashboard") return -1;
            if (b.label === "Dashboard") return 1;
            if (a.label === "Administration") return 1;
            if (b.label === "Administration") return -1;
            return a.label.localeCompare(b.label);
        });

        setOrgNavButtons(orgAppNavBtns);
    }, [organization, isSuperUser, orgId, classes.drawer, classes.drawerPaper, showDrawer, configOptions]);

    const onNavChange = useCallback(
        (selected: number | undefined) => {
            if (selected) {
                setActiveBtn(selected);
            }

            if (selected === undefined) {
                const index = orgNavButtons.findIndex((btn) => location.pathname === btn.buttonProps?.to);
                setActiveBtn(index !== -1 ? index : orgNavButtons.length - 1);
            }

            if (location.pathname.includes(orgNavButtons[selected!]?.buttonProps?.to)) {
                setActiveBtn(selected);
            }

            if (selected && orgNavButtons[selected].isAdmin) {
                setShowDrawer(true);
            } else if (selected === undefined && location.pathname.includes("admin")) {
                const index = orgNavButtons.findIndex((btn) => location.pathname === btn.buttonProps?.to);
                if (orgNavButtons[index]?.isAdmin || index === -1) {
                    setShowDrawer(!showDrawer);
                }
            } else {
                setShowDrawer(false);
            }
        },
        [location, orgNavButtons, showDrawer]
    );

    useEffect(() => {
        const lastOrg = localStorage.getItem("organization:Notifications");
        const lastOrgFormatted = lastOrg && lastOrg!.replace(/['"]+/g, "");

        async function init() {
            if (orgService) {
                const orgId = lastOrgFormatted ? lastOrgFormatted : orgService.orgId;
                const signoutUrl = await userManager.getSignoutUrl(orgId);

                setOrganization(orgService.organization);
                setOrgId(lastOrgFormatted ? lastOrgFormatted : orgId);
                setSignoutUrl(signoutUrl);
            }

            const isSuperUser = await userManager.isSuperUser();

            setForethoughtApiKey(bottle.container.ConfigOptions.forethoughtApiKey);

            setIsSuperUser(isSuperUser);
            setIsLoading(false);
            buildNavigation();
        }
        init();
    }, [orgService, userManager, buildNavigation]);

    const pageOrganization: CppOrganization | undefined = useMemo(() => {
        if (organization != null) {
            return getCppOrganizationFromOrganization(organization);
        }

        return undefined;
    }, [organization]);

    const orgServiceSdk = useMemo(
        () => new NotificationsOrganizationService(apiService, authUser),
        [authUser, apiService]
    );

    useEffect(() => {
        if (orgNavButtons.length > 1 && activeBtn === undefined) {
            const index = orgNavButtons.findIndex((btn) => location.pathname.includes(btn.buttonProps?.to));
            setActiveBtn(index !== -1 ? index : 0);
        }
    }, [orgNavButtons]);

    const onOrgSelect = (org: CppOrganization) => {
        bottle.container.WindowRouter.assign(`${window.location.origin}/${org.name}/${isCitizenView ? "" : "admin"}`);
    };

    const helpMenuOptions = useMemo(() => {
        const menuItems: MenuItem[] = [
            {
                children: "Help Center",
                onClick: () =>
                    window.open(
                        "https://www.platform.civicplus.help/hc/en-us/categories/360004366394-CivicPlus-Notifications"
                    )
            },
            {
                children: "System Status",
                onClick: () =>
                    window.open(
                        "https://www.status.civicplus.help/hc/en-us/community/topics/360000112533-CivicPlus-Platform"
                    )
            }
        ];

        return {
            menuItems,
            show: !isCitizenView
        };
    }, [isCitizenView]);

    const forethoughtProps =
        forethoughtApiKey !== undefined && forethoughtApiKey !== null && forethoughtApiKey !== ""
            ? {
                  apiKey: String(forethoughtApiKey),
                  workflowTag: "Notifications",
                  userId: authUser?.profile?.sub || "",
                  userEmail: authUser?.profile?.email || "",
                  productPageUrl: window.location.href
              }
            : undefined;

    return (
        <Page
            id="civic-notify-page-wrapper"
            title="Notifications • CivicPlus"
            product="notifications"
            currentOrg={pageOrganization}
            onOrgSelect={onOrgSelect}
            orgServiceSdk={orgServiceSdk}
            className={classes.page}
            OrgSwitcherProps={{
                showGeolocation: false,
                showRecentOrganizations: true,
                hide: false,
                superUserRole: undefined
            }}
            helpMenuOptions={helpMenuOptions}
            type={isCitizenView ? "external" : "internal"}
            disableSidebar={true}
            accountMenuOptions={{
                includeDefault: false,
                show: true,
                signoutUrl: `${signoutUrl}`,
                menuItems: [
                    {
                        children: "Reset Password",
                        onClick: () => window.open(userManager.getResetPasswordUrl(), "_blank")
                    },
                    {
                        children: "Settings",
                        onClick: () => window.open(userManager.getSettingsUrl(), "_blank")
                    },
                    {
                        children: "Sign Out",
                        onClick: () => userManager.signOut()
                    }
                ]
            }}
            productNavSections={[
                {
                    title: "Notifications"
                }
            ]}
            navigationRailProps={{
                className: classes.navRail,
                active: activeBtn,
                buttons: orgNavButtons,
                xs: 4,
                onChange: onNavChange,
                "aria-label": "CivicPlus Notifications Navigation"
            }}
            {...(forethoughtProps ? { ForethoughtWidgetProps: forethoughtProps } : null)}
        >
            {isLoading ? <Loader verticallyCenter={true} /> : children}
        </Page>
    );
};

export default PageWrapperInternal;

export const PageWrapper = withRouter(PageWrapperInternal);
